import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../services/api';

const Menu = () => {
  const [menuItems, setMenuItems]         = useState([]);
  const [categories, setCategories]       = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const res = await api.get('/api/menu');
        setMenuItems(res.data);
        const cats = [...new Set(res.data.map(item => item.category))];
        setCategories(['All', ...cats]);
      } catch (error) {
        console.error('Error fetching menu:', error);
      }
    };
    fetchMenu();
  }, []);

  const filtered = activeCategory === 'All'
    ? menuItems
    : menuItems.filter(item => item.category === activeCategory);

  return (
    <div className="bg-ink min-h-screen">

      {/* ── Page Hero ─────────────────────────────────── */}
      <section className="relative h-80 flex items-end overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=1600&q=85"
          alt="Menu header"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/65 to-ink/10"></div>
        <div className="lattice-pattern absolute inset-0 opacity-[0.04]"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pb-14">
          <div className="flex items-center gap-4 mb-3">
            <div className="h-px w-8 bg-warm-gold"></div>
            <span className="text-warm-gold text-xs tracking-[0.4em] uppercase">Crafted with Passion</span>
          </div>
          <h1 className="font-serif text-6xl md:text-7xl text-white leading-none">
            Our <span className="text-gradient-gold">Menu</span>
          </h1>
        </div>
      </section>

      {/* ── Sticky Category Filter ─────────────────────── */}
      <div className="sticky top-0 z-30 bg-ink/95 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-shrink-0 px-6 py-2 text-xs tracking-[0.2em] uppercase font-medium transition-all duration-300 ${
                  activeCategory === cat
                    ? 'bg-imperial-red text-white'
                    : 'border border-white/15 text-white/50 hover:border-warm-gold/60 hover:text-warm-gold'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Menu Grid ─────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-6 py-20">

        {/* Count line */}
        <div className="flex items-center gap-4 mb-12">
          <div className="h-px flex-1 bg-white/5"></div>
          <span className="text-white/25 text-xs tracking-[0.3em] uppercase">
            {filtered.length} Dishes
          </span>
          <div className="h-px flex-1 bg-white/5"></div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(item => (
            <div key={item.id} className="dish-card group overflow-hidden">

              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <img
                  src={item.image_url}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/10 to-transparent"></div>

                {/* Category pill */}
                <div className="absolute top-3 left-3 bg-imperial-red/90 text-white text-xs px-3 py-1 tracking-[0.15em] uppercase">
                  {item.category}
                </div>

                {/* Gold reveal bar */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-warm-gold transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
              </div>

              {/* Body */}
              <div className="p-5">
                <h3 className="font-serif text-lg text-white mb-1.5">{item.title}</h3>
                <p className="text-white/35 text-sm leading-relaxed mb-5 line-clamp-2">{item.description}</p>
                <div className="flex items-center justify-between pt-4 border-t border-white/5">
                  <span className="font-serif text-2xl text-warm-gold">${item.price}</span>
                  <span className={`text-xs tracking-[0.2em] uppercase px-3 py-1 ${
                    item.is_available
                      ? 'text-emerald-400/80 bg-emerald-400/8 border border-emerald-400/15'
                      : 'text-white/20 bg-white/3 border border-white/8'
                  }`}>
                    {item.is_available ? 'Available' : 'Unavailable'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty state */}
        {filtered.length === 0 && (
          <div className="text-center py-24">
            <div className="text-5xl mb-4 opacity-30">🍜</div>
            <p className="text-white/25 text-sm tracking-widest uppercase">No dishes in this category</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Menu;
